import { derived, readable } from "svelte/store";
import { userStore } from "./user";
import { params } from "svelte-spa-router";
import {
  chainStores,
  getDocumentAsStore,
  getSubCollectionAsStore,
  mergeLatestOfEachToArray,
} from "$lib/utils/firestore";
import { selectedOrgIdStore } from "./orgs";

const rawTeamsStore = chainStores(userStore, [], (user) => {
  if (!user) {
    return readable([]);
  }
  const teamStores = user.teamClaims.map((teamRef) => {
    return getDocumentAsStore({ docPath: `teams/${teamRef.teamId}`, extraData: { claims: teamRef.claims } });
  });
  return derived(mergeLatestOfEachToArray(teamStores), (teams) => {
    return teams
      .filter((t) => t !== undefined)
      .map((t) => {
        return {
          teamId: t.id,
          data: t.data,
          claims: t.claims,
        };
      });
  });
});

export interface TeamRef {
  id: string;
  alias: string;
}

export const teamsListStore = derived(rawTeamsStore, (rawTeams) => {
  return rawTeams.map<TeamRef>((rawTeam) => {
    return {
      id: rawTeam.teamId,
      alias: rawTeam.data.alias as string,
    };
  });
});

teamsListStore.subscribe((u) => console.log("TeamRefList store changed", u));

export interface TeamMember {
  email: string;
  claims: string[];
  modified: Date;
}

export interface TeamApp {
  id: string;
  description: string;
  modified: Date;
  redirectUris: string[];
}
export interface SelectedTeam {
  id: string;
  alias?: string;
  userClaims: string[];
  members: TeamMember[];
  apps: TeamApp[];
}

export const selectedTeamStore = chainStores(
  derived([rawTeamsStore, params, selectedOrgIdStore], (both) => both),
  undefined,
  ([rawTeams, currentParams, selectedOrgId]) => {
    if (!rawTeams || rawTeams.length === 0) {
      return readable(undefined);
    }
    let team = rawTeams.find((rt) => rt.teamId === currentParams?.teamId);
    if (!team && selectedOrgId) {
      // if no team is selected and there is an org selected, then don't select a team
      return readable(undefined);
    }
    if (!team) {
      // if no team is selected and there is no org selected, then select the first team
      team = rawTeams[0];
    }

    return derived(
      [getSubCollectionAsStore(`teams/${team.teamId}/members`), getSubCollectionAsStore(`teams/${team.teamId}/apps`)],
      ([memberItems, appItems]) => {
        const selectedTeam: SelectedTeam = {
          id: team.teamId,
          alias: team.data.alias,
          userClaims: team.claims,
          members: memberItems.map((i) => {
            return {
              email: i.id,
              claims: i.data.claims as string[],
              modified: i.data.modified.toDate() as Date,
            };
          }),
          apps: appItems.map((i) => {
            return {
              id: i.id,
              description: i.data.description as string,
              modified: i.data.modified.toDate() as Date,
              redirectUris: i.data.redirect_uris as string[],
            };
          }),
        };
        return selectedTeam;
      },
    );
  },
);

selectedTeamStore.subscribe((r) => console.log("Selected Team store changed", r));
